import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';

import { useAudio, useAuth, useChatData, useConfig } from '@chainlit/react-client';

import AudioPresence from '@/components/AudioPresence';
import ButtonLink from '@/components/ButtonLink';
import { Settings } from '@/components/icons/Settings';
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';

import { useSidebar } from '@/components/ui/sidebar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Translator } from 'components/i18n';

import { chatSettingsSidebarOpenState } from '@/state/project';

import ApiKeys from './ApiKeys';
import ChatProfiles from './ChatProfiles';
import NewChatButton from './NewChat';
import ShareButton from './Share';
import SidebarTrigger from './SidebarTrigger';
import UserNav from './UserNav';

const Header = memo(() => {
  const { audioConnection } = useAudio();
  const navigate = useNavigate();
  const { data } = useAuth();
  const { config } = useConfig();
  const { chatSettingsInputs } = useChatData();
  const { open, openMobile, isMobile } = useSidebar();
  const setChatSettingsSidebarOpen = useSetRecoilState(
    chatSettingsSidebarOpenState
  );

  const sidebarOpen = isMobile ? openMobile : open;

  const historyEnabled = data?.requireLogin && config?.dataPersistence;
  const sidebarHidden = config?.ui?.default_sidebar_state === 'hidden';

  const links = config?.ui?.header_links || [];

  const showSettingsInHeader =
    config?.ui?.chat_settings_location === 'sidebar' &&
    chatSettingsInputs.length > 0;

  return (
    <div
      className="p-3 flex h-[60px] items-center justify-between gap-2 relative"
      id="header"
    >
      <div className="flex items-center">
        {historyEnabled && !sidebarHidden ? !sidebarOpen ? <SidebarTrigger /> : null : null}
        {historyEnabled && !sidebarHidden ? (
          !sidebarOpen ? (
            <NewChatButton navigate={navigate} />
          ) : null
        ) : (
          <NewChatButton navigate={navigate} />
        )}

        <ChatProfiles navigate={navigate} />
      </div>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        {audioConnection === 'on' ? (
          <AudioPresence
            type="server"
            height={35}
            width={70}
            barCount={4}
            barSpacing={2}
          />
        ) : null}
      </div>

      <div />

      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center gap-1">
        {links &&
          links.map((link, index) => (
            <ButtonLink
              key={`${link.name}-${link.url}-${index}`}
              name={link.name}
              displayName={link.display_name}
              iconUrl={link.icon_url}
              url={link.url}
              target={link.target}
              type={link.type as any}
            />
          ))}
        {showSettingsInHeader && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                id="chat-settings-header-button"
                onClick={() => setChatSettingsSidebarOpen(true)}
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-muted-foreground"
              >
                <Settings className="!size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <Translator path="chat.settings.title" />
            </TooltipContent>
          </Tooltip>
        )}
        <ApiKeys />
        <ShareButton />
        <UserNav />
      </div>

      {/* Mobile Navigation */}
      <div className="flex md:hidden items-center">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-muted-foreground">
              <Menu className="!size-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="flex flex-col gap-4 pt-10 px-4 w-[280px]">
            {links && links.length > 0 && (
              <div className="flex flex-col gap-2 border-b pb-4">
                {links.map((link, index) => (
                  <ButtonLink
                    key={`${link.name}-${link.url}-${index}`}
                    name={link.name}
                    displayName={link.display_name}
                    iconUrl={link.icon_url}
                    url={link.url}
                    target={link.target}
                    type={link.type as any}
                    className="w-full justify-start"
                    mobile
                  />
                ))}
              </div>
            )}

            <div className="flex flex-col gap-2 border-b pb-4">
              {showSettingsInHeader && (
                <Button
                  id="chat-settings-mobile-button"
                  onClick={() => setChatSettingsSidebarOpen(true)}
                  variant="ghost"
                  className="w-full justify-start text-muted-foreground hover:text-foreground"
                >
                  <Settings className="mr-2 !size-5" />
                  <Translator path="chat.settings.title" />
                </Button>
              )}

              <div className="flex flex-col gap-2">
                <ApiKeys mobile />
                <ShareButton mobile />
              </div>
            </div>

            <div className="mt-auto pb-4">
              <UserNav mobile />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
});

export { Header };
